import toast, { Toaster } from "react-hot-toast"
import { Button } from "../components/button"
import { useTranslation } from "react-i18next";
import BackButtonWrapper from "../components/button/BackButton/wrapper";
import useFormWithSchema from "../hooks/useFormWithSchema";
import * as Yup from "yup";
import { createRef, MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import FileUploadInput from "../components/inputs/FileUploadInput";
import { allowedFileTypes, userProfileAttachmentKeys, USER_PROFILE_ATTACHMENT_KEYS, USER_PROFILE_ATTACHMENTS_TYPE } from "../constants/common";
import ViewAttachmentModal from "../components/modal/ViewAttachmentModal";
import { useAddUpdateProfile } from "../hooks/user/mutation";
import i18n from "../i18n";
import { fileToBase64 } from "../lib/functions";
import { useGetProfile } from "../hooks/user/query";
import Loader from "../components/loader";

const createEditProfileSchema = (isAttachments:
    { haveNationalID: number; haveHostEmployeeCard: number; }
) => {
    return Yup.object({
        uploadNationalID: Yup.mixed().when([], {
            is: () => !isAttachments?.haveNationalID,
            then: (schema) =>
                schema.required(i18n.language === "en" ? "National ID is required" : "رقم الهوية الوطنية مطلوب"),
            otherwise: (schema) => schema.notRequired(),
        }),
        uploadHostEmployeeCard: Yup.mixed().when([], {
            is: () => !isAttachments?.haveHostEmployeeCard,
            then: (schema) =>
                schema.required(i18n.language === "en" ? "Host Employee Card is required" : "رقم بطاقة الموظف مطلوب"),
            otherwise: (schema) => schema.notRequired(),
        }),
    })
}

const EditProfile = () => {
    const { t } = useTranslation();

    const [isAttachments, setIsAttachments] = useState({
        haveNationalID: 0,
        haveHostEmployeeCard: 0,
    });

    const { data: profileData, isPending: isProfileLoading } = useGetProfile();

    const { mutateAsync: AddUpdateEditProfile, isPending: isAddUpdateEditProfileLoading } = useAddUpdateProfile();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useFormWithSchema(createEditProfileSchema(isAttachments));

    const uploadRefs = useRef<
        Record<string, MutableRefObject<HTMLInputElement | null>>
    >(
        userProfileAttachmentKeys.reduce(
            (acc, key) => {
                acc[key] = createRef<HTMLInputElement>();
                return acc;
            },
            {} as Record<string, MutableRefObject<HTMLInputElement | null>>,
        ),
    );

    const [fileNames, setFileNames] = useState<Record<string, string>>(
        userProfileAttachmentKeys.reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
    );

    const [fileTypes, setFileTypes] = useState<Record<string, string>>(
        userProfileAttachmentKeys.reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
    );

    const [filesBase64, setFilesBase64] = useState<Record<string, string>>(
        userProfileAttachmentKeys.reduce((acc, key) => ({ ...acc, [key]: "" }), {}),
    );

    const setFileName = (key: string, value: React.SetStateAction<string>) => {
        setFileNames((prev) => ({
            ...prev,
            [key]: typeof value === "function" ? value(prev[key]) : value,
        }));
    };

    const setFileType = (key: string, value: React.SetStateAction<string>) => {
        setFileTypes((prev) => ({
            ...prev,
            [key]: typeof value === "function" ? value(prev[key]) : value,
        }));
    };

    useEffect(() => {
        if (profileData) {
            setIsAttachments({
                haveNationalID: profileData.s_HostNationalIDFile,
                haveHostEmployeeCard: profileData.s_HostEmployeeCardFile,
            });

            setFileTypes((prev) => ({
                ...prev,
                nationalId: profileData.s_HostNationalIDContentType || "",
                hostEmployeeCard: profileData.s_HostEmployeeCardContentType || "",
            }));

            setFilesBase64((prev) => ({
                ...prev,
                nationalId: profileData.s_HostNationalIDFile || "",
                hostEmployeeCard: profileData.s_HostEmployeeCardFile || "",
            }));
        }
    }, [profileData])

    const [selectedType, setSelectedType] = useState(0);
    const [previewFiles, setPreviewFiles] = useState<
        Record<number, { url: string; contentType: string }>
    >({});
    const [imageSrc, setImageSrc] = useState("");
    const [contentType, setContentType] = useState("");
    const [isViewAttachmentModalOpen, setIsViewAttachmentModalOpen] = useState(false);

    const onSubmit = useCallback(async (data: any) => {
        if (data && Object.keys(data).length === 0) {
            toast.error(t("noAttachmentUploaded"));
            return;
        }

        try {
            const body = {
                s_HostEmployeeCardFile: data.uploadHostEmployeeCard
                    ? await fileToBase64(data.uploadHostEmployeeCard)
                    : '',
                s_HostEmployeeCardContentType: data.uploadHostEmployeeCard ? data.uploadHostEmployeeCard.type : '',
                s_HostNationalIDFile: data.uploadNationalID
                    ? await fileToBase64(data.uploadNationalID)
                    : '',
                s_HostNationalIDContentType: data.uploadNationalID ? data.uploadNationalID.type : '',
            }

            await AddUpdateEditProfile(body);

            toast.success(t("profileUpdatedSuccessfully"));
        } catch (error) {
            toast.error(t("somethingWentWrong"));
        }
    }, [
        AddUpdateEditProfile,
        t,
        isAttachments.haveNationalID,
        isAttachments.haveHostEmployeeCard,
    ]);

    const handleAttachmentView = useCallback(
        async (type: number) => {
            try {
                // If we already have a local preview for this attachment type
                if (previewFiles && previewFiles[type]) {
                    setImageSrc(previewFiles[type].url);
                    setContentType(previewFiles[type].contentType);
                    setIsViewAttachmentModalOpen(true);
                    return;
                }

                setImageSrc(
                    type === USER_PROFILE_ATTACHMENTS_TYPE.NATIONAL_ID
                        ? `data:${fileTypes.nationalId};base64,${filesBase64.nationalId}`
                        : `data:${fileTypes.hostEmployeeCard};base64,${filesBase64.hostEmployeeCard}`
                );
                setContentType(
                    type === USER_PROFILE_ATTACHMENTS_TYPE.NATIONAL_ID
                        ? fileTypes.nationalId
                        : fileTypes.hostEmployeeCard
                );
                setIsViewAttachmentModalOpen(true);
            } catch (error: any) {
                toast.error(t("somethingWentWrong"));
            } finally {
                setSelectedType(0);
            }
        },
        [previewFiles, t, fileTypes, filesBase64],
    );

    if (isProfileLoading) {
        return (
            <div className="flex justify-center">
                <Loader />
            </div>
        )
    }

    return (
        <>
            <Toaster position="top-right" reverseOrder={false} />

            <div className="bg-white p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
                <BackButtonWrapper isDarkModeStyles={false}>
                    <h1 className="text-2xl">{t("editProfile")}</h1>
                </BackButtonWrapper>

                <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <FileUploadInput
                            name="uploadNationalID"
                            label={t("uploadNationalID")}
                            accept=".png,.jpg,.jpeg,.pdf"
                            allowedTypes={allowedFileTypes}
                            isAttachments={isAttachments?.haveNationalID}
                            control={control}
                            uploadRef={uploadRefs.current[USER_PROFILE_ATTACHMENT_KEYS.NATIONAL_ID]}
                            fileName={fileNames[USER_PROFILE_ATTACHMENT_KEYS.NATIONAL_ID]}
                            setFileName={(val) =>
                                setFileName(USER_PROFILE_ATTACHMENT_KEYS.NATIONAL_ID, val)
                            }
                            setFileType={(val) =>
                                setFileType(USER_PROFILE_ATTACHMENT_KEYS.NATIONAL_ID, val)
                            }
                            handleAttachmentView={handleAttachmentView}
                            selectedType={selectedType}
                            attachmentType={USER_PROFILE_ATTACHMENTS_TYPE.NATIONAL_ID}
                            error={errors.uploadNationalID}
                            previewFiles={previewFiles}
                            setPreviewFiles={setPreviewFiles}
                        />

                        <FileUploadInput
                            name="uploadHostEmployeeCard"
                            label={t("uploadEmployeeCard")}
                            accept=".png,.jpg,.jpeg,.pdf"
                            allowedTypes={allowedFileTypes}
                            isAttachments={isAttachments?.haveHostEmployeeCard}
                            control={control}
                            uploadRef={uploadRefs.current[USER_PROFILE_ATTACHMENT_KEYS.HOST_EMPLOYEE_CARD]}
                            fileName={fileNames[USER_PROFILE_ATTACHMENT_KEYS.HOST_EMPLOYEE_CARD]}
                            setFileName={(val) =>
                                setFileName(USER_PROFILE_ATTACHMENT_KEYS.HOST_EMPLOYEE_CARD, val)
                            }
                            setFileType={(val) =>
                                setFileType(USER_PROFILE_ATTACHMENT_KEYS.HOST_EMPLOYEE_CARD, val)
                            }
                            handleAttachmentView={handleAttachmentView}
                            selectedType={selectedType}
                            attachmentType={USER_PROFILE_ATTACHMENTS_TYPE.HOST_EMPLOYEE_CARD}
                            // isGetFileByTypeLoading={isGetFileByTypeLoading}
                            error={errors.uploadHostEmployeeCard}
                            previewFiles={previewFiles}
                            setPreviewFiles={setPreviewFiles}
                            required
                        />
                    </div>

                    <Button
                        isLoading={isAddUpdateEditProfileLoading}
                        className=" text-slate-50 sm:max-w-max rounded-xl font-bold w-full"
                    >
                        {t("save")}
                    </Button>
                </form>
            </div>

            <ViewAttachmentModal
                attachment={imageSrc}
                contentType={contentType}
                isOpen={isViewAttachmentModalOpen}
                onOpenChange={() => {
                    setIsViewAttachmentModalOpen(false);
                    setImageSrc("");
                }}
            />
        </>
    )
}

export default EditProfile
